import { isNaN, merge } from "es-toolkit/compat";
import { config } from "../config.ts";
import { isSmallNumber } from "./precision.ts";
import { parseNumber } from "./shape.ts";
import { isFiniteNumber } from "./validation.ts";

/**
 * Get the details to help format a number, one of which are options to pass to
 * Intl.NumberFormat()
 * @param value the number to get the formatting options for
 * @param opts formatting levers
 * @see {@link isSmallNumber} used underneath to check if the number is small
 * @returns an object with the options to pass into Intl.NumberFormat() and a
 * boolean indicating if the number is small
 */
export const getNumberFormatOptions = (
  value: number,
  opts?: {
    /** the locale to format in the context of  */
    locale?: Intl.LocalesArgument;
    /** the # of fraction digits to show (digits after the decimal point) */
    fractionDigits?: number;
    /** separate every thousandth significant digit? (e.g using commas like
     * 1,000,000. note, delimiter is locale specific) default is true */
    hasThousandsSeparators?: boolean;
    /** show very small numbers using exponential notation instead? default is
     * true */
    showSmallNumberAsExponential?: boolean;
    /** the threshold above or below 0 up to this number, to consider "small".
     * default is 0.001 */
    smallNumberThreshold?: number;
    /** override any of the opts to pass to Intl.NumberFormat() generated by
     * this utility */
    overrideOpts?: Intl.NumberFormatOptions;
  }
): {
  /** is the provided value an integer or a number (integer or float) */
  type: "integer" | "number";
  /** is the provided value a small number? */
  isSmallNumber: boolean;
  /** the locale to format the number in */
  locale: Intl.LocalesArgument;
  /** the options to pass to Intl.NumberFormat() */
  opts: Intl.NumberFormatOptions;
} | null => {
  const {
    locale,
    fractionDigits,
    hasThousandsSeparators,
    showSmallNumberAsExponential,
    smallNumberThreshold,
    overrideOpts,
  } = merge(
    {
      locale: config.locale,
      hasThousandsSeparators: true,
      showSmallNumberAsExponential: true,
      smallNumberThreshold: 0.01,
    },
    opts
  );

  if (!isFiniteNumber(value)) return null;

  const isSmallNum = isSmallNumber(value, smallNumberThreshold);
  const type = Number.isInteger(value) ? "integer" : "number";

  return {
    type,
    isSmallNumber: isSmallNum,
    locale,
    opts: {
      ...(Number.isInteger(fractionDigits)
        ? {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          }
        : {}),
      notation:
        showSmallNumberAsExponential && isSmallNum ? "scientific" : "standard",
      useGrouping: hasThousandsSeparators,
      ...overrideOpts,
    },
  };
};

/**
 * Format a number for displaying
 * @param value the value to format
 * @param opts formatting levers
 * @see {@link parseNumber} used underneath to parse the value as a number
 * @see {@link getNumberFormatOptions} used underneath to get the number format options
 * @see {@link Intl.NumberFormat} used underneath to format the parsed number
 * @returns a string representation of the provided number
 */
export const formatNumber = (
  value: unknown,
  formattingOpts?: Parameters<typeof getNumberFormatOptions>[1],
  parsingOpts?: {
    /** force unsafe numbers to become safely representable? */
    shouldBeSafe?: boolean;
  }
): string | null => {
  const { locale, ...restOpts } = formattingOpts ?? {};
  const { shouldBeSafe } = parsingOpts ?? {};
  const valueAsNumber = parseNumber(value, { shouldBeSafe });
  const numberOpts = getNumberFormatOptions(valueAsNumber, restOpts);

  if (isNaN(valueAsNumber)) return null;

  return Intl.NumberFormat(
    locale ?? numberOpts?.locale ?? config.locale,
    numberOpts?.opts
  ).format(valueAsNumber);
};

/**
 * Format a number and unit for displaying
 * @param value the value and optional unit if any to format
 * @param opts formatting levers
 * @see {@link formatNumber} used underneath to format the number
 * @returns a string representation of the provided number and unit
 */
export const formatNumberValueAndUnit = (
  /** representation of the value with the value and unit */
  valueAndUnitObj: {
    /** primitive number value to format */
    value: number;
    /** the unit to display */
    unit?: string | null;
  },
  /** optional formatting levers */
  opts?: Parameters<typeof getNumberFormatOptions>[1] & {
    shouldFormat?: boolean;
  }
): string | null => {
  const { value, unit } = valueAndUnitObj;
  const { shouldFormat = true, ...restOpts } = opts ?? {};

  if (shouldFormat) {
    const formattedNumber = formatNumber(value, restOpts);

    return formattedNumber !== null
      ? `${formattedNumber}${typeof unit === "string" && unit ? " " + unit : ""}`
      : formattedNumber;
  } else {
    const parsedNumber = parseNumber(value, { shouldBeSafe: true });

    return isNaN(parseNumber)
      ? null
      : `${String(
          parsedNumber
        )}${typeof unit === "string" && unit ? " " + unit : ""}`;
  }
};

/**
 * Format a number to fixed notation, only if it's a floating point number
 * @param value the value to fix
 * @param fractionDigits the # of decimal digits to fix to
 * @returns the formatted number
 */
export function formatNumberToFixedIfNotInteger(
  value: number,
  fractionDigits: number
): string | null {
  const valueAsNumber = parseNumber(value, { shouldBeSafe: true });
  const numberOpts = getNumberFormatOptions(valueAsNumber, { fractionDigits });

  if (isNaN(valueAsNumber)) return null;

  return Intl.NumberFormat(
    numberOpts?.locale ?? config.locale,
    numberOpts?.type === "integer"
      ? {
          ...(numberOpts?.opts ?? {}),
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }
      : numberOpts?.opts
  ).format(valueAsNumber);
}

/**
 * Format a number to fixed notation, preserving high precision
 * @param value the value to fix
 * @param opts formatting levers
 * @returns the formatted number
 */
export function formatNumberToFixedPrecise(
  value: number,
  opts?: {
    minFractionDigits?: number;
    maxFractionDigits?: number;
  }
): string {
  const { minFractionDigits, maxFractionDigits } = merge(
    { minFractionDigits: 14, maxFractionDigits: 20 },
    opts ?? {}
  );
  const valueFixedMax = value.toFixed(maxFractionDigits);
  const valueFixedMin = value.toFixed(minFractionDigits);

  const zero = 0;
  const zeroFixedMax = zero.toFixed(maxFractionDigits);
  const zeroFixedMin = zero.toFixed(minFractionDigits);

  if (value !== 0) {
    if (valueFixedMax === zeroFixedMax) {
      console.warn(
        `Truncation error: ${value} -> ${valueFixedMax}, returning ${valueFixedMin}`
      );
      return valueFixedMin;
    } else if (valueFixedMin === zeroFixedMin) {
      console.warn(
        `Potential truncation error: ${value} -> ${valueFixedMin}, returning ${valueFixedMax}`
      );
      return valueFixedMax;
    }
  }

  return valueFixedMax;
}
